import { BankOutlined, GlobalOutlined } from '@ant-design/icons';
import { Form, Input } from 'antd';
import { BankAccountsGroup } from '@/components/bank-accounts-group';
import { SettingSwitch } from '@/components/profile/settingCards';
import { cn } from '@/libs/utils';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';

// Parts may be stored as "undefined"/"null" text; only digits count.
const digits = (part) => {
  const s = String(part ?? '').trim();
  return /^\d+$/.test(s) ? s : '';
};

/**
 * The "Payment" tile of the employee profile: the account the salary goes
 * to, as one number to read (the three fields to edit it), the bank and the
 * residency. Same fields, rules and save as EmployeePersonalFields, whose
 * bank fields step aside on the profile.
 */
export default function EmployeePaymentProfilePanel() {
  const {
    bankAccountsKeys,
    bankAccountsState,
    bankNameKeys,
    bankNameState,
    form,
    isResidentKeys,
    isResidentState,
    readOnly,
    sectionClass,
    t,
  } = useEmployeeGeneralDataForm();
  const f = (key) => t(key, { ns: 'forms' });
  const tp = (key) =>
    t(`employeeProfile.paymentUi.${key}`, { ns: 'translations' });

  const account =
    Form.useWatch(bankAccountsKeys, { form, preserve: true }) || {};
  const parts = [account.part1, account.part2, account.part3].map(digits);
  const accountText = parts.every(Boolean) ? parts.join('-') : '';

  return (
    <div className={cn('grid max-w-[620px] gap-4', sectionClass('isplata'))}>
      {bankAccountsState?.visible && (
        <section>
          <h3 className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.08em] text-zinc-400">
            {tp('accountTitle')}
          </h3>
          <div className="setting-card rounded-xl border border-zinc-200 bg-white p-3">
            {readOnly && (
              <div
                className={cn(
                  'font-mono text-[20px] font-bold tracking-wide',
                  accountText ? 'text-zinc-900' : 'italic text-zinc-300'
                )}
              >
                {accountText || tp('noAccount')}
              </div>
            )}
            {/* Kept mounted while viewing (only hidden), so the form never
                sees the account as missing. */}
            <Form.Item
              name={bankAccountsKeys}
              label={f('bankAccountNumber')}
              className="!mb-1"
              hidden={readOnly}
            >
              <BankAccountsGroup
                form={form}
                name={bankAccountsKeys[1]}
                nestedName={bankAccountsKeys[0]}
                disabled={bankAccountsState?.disabled}
              />
            </Form.Item>
          </div>
        </section>
      )}

      <div className="grid grid-cols-2 gap-2">
        {bankNameState?.visible && (
          <div
            className={cn(
              'setting-card flex items-center gap-2.5 rounded-xl border border-zinc-200 bg-white p-2.5',
              !isResidentState?.visible && 'col-span-2'
            )}
          >
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[rgba(var(--accentColor),0.1)] text-[15px] text-accent">
              <BankOutlined />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-[11.5px] font-semibold text-zinc-500">
                {f('bankName')}
              </div>
              <Form.Item name={bankNameKeys} noStyle>
                <Input
                  size="small"
                  disabled={bankNameState?.disabled}
                  placeholder={f('bankName')}
                />
              </Form.Item>
            </div>
          </div>
        )}
        {isResidentState?.visible && (
          <Form.Item name={isResidentKeys} noStyle>
            <SettingSwitch
              locked={readOnly || isResidentState?.disabled}
              icon={<GlobalOutlined />}
              title={f('resident')}
              wide={!bankNameState?.visible}
            />
          </Form.Item>
        )}
      </div>
    </div>
  );
}
